/**
 * 캠핑 허브 [장비] 탭의 목록(09-29).
 *
 * 예전엔 [장비] 탭이 일반 검색 결과(`products`)를 그대로 그렸다 — 지도 영역을 바꾸거나
 * 지우면 결과를 다시 받아야 했고, 범위를 지운 주소는 라우트가 받지 않아 **옛 결과가 남았다**.
 * 지금은 두 갈래를 잇는다:
 *  1) 허브 장비 — 이미 메모리에 있는 허브 데이터셋에서, 지점 목록과 **같은 지점 집합**
 *     (범위 적용)의 상품. 요청이 없다. 순서는 지점 목록(가까운 순)에서 처음 만난 순.
 *  2) 그 밖의 장비 — 허브 데이터셋에 없는 상품(캠핑장에 연결되지 않은 택배·배송 상품).
 *     일반 검색 결과에서만 알 수 있어, 탭을 열 때만 받아 뒤에 붙인다.
 */
import type { Product } from "@/types";
import { expandCategoryCodes } from "@/config/categories";

export interface GearEntry {
  product: Product;
  /** 이 장비를 받을 수 있는 (가장 가까운) 캠핑장 — 상세 링크의 `?branch=`. 택배 상품은 없음. */
  stationId?: string;
}

/**
 * 지점들(이미 원하는 순서) → 장비. 같은 상품이 여러 캠핑장에 있으면 처음 만난 캠핑장 하나.
 */
export function collectHubGear(
  stations: ReadonlyArray<{ id: string; products: readonly Product[] }>,
): GearEntry[] {
  const seen = new Set<string>();
  const out: GearEntry[] = [];
  for (const station of stations) {
    for (const product of station.products) {
      if (seen.has(product.id)) continue;
      seen.add(product.id);
      out.push({ product, stationId: station.id });
    }
  }
  return out;
}

/**
 * 지도 범위 밖 캠핑장에만 있지만 택배로 받을 수 있는 허브 장비(범위 무관 전체 지점에서).
 * 범위로 좁히면 택배 장비까지 사라지던 문제(2026-10-01) — 택배는 어느 지역에서든 받으므로 남긴다.
 * 이미 목록에 있는 상품(`exclude`)은 빼고, 캠핑장을 고르지 않으므로 stationId 는 없다.
 */
export function shippableHubGear(
  stations: ReadonlyArray<{ products: readonly Product[] }>,
  shippable: ReadonlySet<string>,
  exclude: ReadonlySet<string>,
): GearEntry[] {
  if (shippable.size === 0) return [];
  const seen = new Set<string>();
  const out: GearEntry[] = [];
  for (const station of stations) {
    for (const product of station.products) {
      if (!shippable.has(product.id) || exclude.has(product.id) || seen.has(product.id)) continue;
      seen.add(product.id);
      out.push({ product });
    }
  }
  return out;
}

/** 허브 데이터셋 전체(범위 무관)의 상품 id — "그 밖의 장비"를 가려내는 기준. */
export function hubProductIds(
  stations: ReadonlyArray<{ products: readonly Product[] }>,
): Set<string> {
  const ids = new Set<string>();
  for (const station of stations) {
    for (const product of station.products) ids.add(product.id);
  }
  return ids;
}

/**
 * 일반 검색 결과 중 허브 데이터셋에 없는 상품만(중복 제거). 허브 상품은 범위 밖이라
 * 1) 에서 빠졌더라도 여기로 되살리지 않는다 — 지점 목록과 같은 집합을 지킨다.
 */
export function extraGear(
  products: readonly Product[],
  hubIds: ReadonlySet<string>,
): GearEntry[] {
  const seen = new Set<string>();
  const out: GearEntry[] = [];
  for (const product of products) {
    if (hubIds.has(product.id) || seen.has(product.id)) continue;
    seen.add(product.id);
    out.push({ product });
  }
  return out;
}

/**
 * 지도 영역 검색 결과(`/api/search/area`)를 고른 카테고리로 좁힌다(10-13).
 * 그 경로는 카테고리를 서버에서 거르지 않고 페이지가 화면에서 거르는데, [장비] 탭이 따로
 * 받은 결과를 그대로 쓰면 "현 위치에서 검색" 뒤 다른 카테고리 상품이 장비로 섞였다.
 * `categories` 는 URL 값 그대로("10-04-02" 또는 쉼표 목록) — 없으면 거르지 않는다.
 */
export function gearInCategories(
  products: readonly Product[],
  categories: string | null,
): Product[] {
  const codes = (categories ?? "").split(",").map((c) => c.trim()).filter(Boolean);
  if (codes.length === 0) return [...products];
  const allowed = new Set(expandCategoryCodes(codes));
  return products.filter((product) => !!product.category_code && allowed.has(product.category_code));
}
